import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';

// Selectors
import { getDraftFeedbackByWorkspaceUuid } from 'Shared/selectors/Feedbacks.selectors';

// Types
import { FeedbackRecognitionTemplateTypes } from 'Shared/constants/types';

// Hooks
import { useDeepEqualSelector, useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Constants
import { clickedFeedbackFormEvent } from 'Shared/constants/constants';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Styles
import './FeedbackCreationHeader.scss';
import moment from 'moment';

type Props = {
  isEditFeedback: boolean;
  isSubmitDisabled?: boolean;
  pristine: boolean;
  submitting: {
    isDraftSaving: boolean;
    isSubmitting: boolean;
  };
  templateType: string;
  isFeedbackRequested: boolean;
  hasVideo: boolean;
  onDeleteDraftClick?: (event: React.MouseEvent) => void;
  onSaveAsDraftClick: () => void;
  onSubmitFeedbackClick: () => void;
  allowSaveAsDraft?: boolean;
  handleBack?: () => void;
  handleNext?: () => void;
};

function FeedbackCreationHeader({
  isEditFeedback,
  isSubmitDisabled = false,
  submitting,
  pristine,
  onDeleteDraftClick,
  onSaveAsDraftClick,
  onSubmitFeedbackClick,
  templateType,
  isFeedbackRequested,
  hasVideo,
  allowSaveAsDraft = true,
  handleBack,
  handleNext,
}: Props) {
  const params = useParams();
  const [startTime] = useState(moment());

  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useToggle(false);

  const { workspaceId, feedbackUuid, feedbackRecognitionRequestId } = params;

  const { id: currentUserId } = useCurrentUser();

  const draftFeedback = useDeepEqualSelector(getDraftFeedbackByWorkspaceUuid(workspaceId));

  const isFeedbackDraft = feedbackUuid && draftFeedback?.uuid === feedbackUuid;

  const isGPT3Template = templateType === FeedbackRecognitionTemplateTypes.feedbackGPT3Coach;

  const teammate = useSelector(state => {
    const workspaceMembers = state.workspace.users;
    return workspaceMembers?.find(({ id }) => id !== currentUserId);
  });

  const backLinkUrl = isEditFeedback
    ? PageUrlsUtil.makeWorkspaceFeedbacItemUrl(workspaceId, feedbackUuid)
    : PageUrlsUtil.makeFeedbackRequestUrl(workspaceId, feedbackRecognitionRequestId);

  const history = useHistory();

  const handleBackClick = useCallback(() => {
    (window as any).analytics.track(clickedFeedbackFormEvent, { target: 'back_button', templateType });
    if (handleBack) handleBack();
    else history.push(backLinkUrl);
  }, [history, handleBack, backLinkUrl, templateType]);

  const handleDeleteDraftClick = useCallback(
    event => {
      (window as any).analytics.track(clickedFeedbackFormEvent, { target: 'delete_draft_button', templateType });
      onDeleteDraftClick(event);
    },
    [onDeleteDraftClick, templateType]
  );

  const handleSaveAsDraftClick = useCallback(() => {
    (window as any).analytics.track(clickedFeedbackFormEvent, { target: 'save_draft_button', templateType });
    onSaveAsDraftClick();
  }, [onSaveAsDraftClick, templateType]);

  const handleSubmitFeedbackClick = useCallback(() => {
    const timeSpent = moment().diff(startTime, 'minutes');
    (window as any).analytics.track('Clicked Submit Feedback', {
      isDraft: Boolean(isFeedbackDraft),
      isEdit: Boolean(isEditFeedback),
      templateType,
      isFeedbackRequested,
      hasVideo,
      minutesSpent: timeSpent,
    });
    onSubmitFeedbackClick();
  }, [onSubmitFeedbackClick, isFeedbackDraft, templateType, isFeedbackRequested]);

  const showSaveAsDraftButton =
    allowSaveAsDraft && ((isEditFeedback && isFeedbackDraft) || (!isEditFeedback && !draftFeedback));

  const saveAsDraftDisabled = submitting.isSubmitting || pristine || isSubmitDisabled;

  const submitFeedbackDisabled =
    submitting.isDraftSaving || (!isFeedbackDraft && !isGPT3Template && pristine) || isSubmitDisabled;

  const showSubmit = !handleNext;

  const submitFeedbackButtonLabel = () => {
    if (!isEditFeedback || (isEditFeedback && isFeedbackDraft))
      return (
        <>
          Submit<span className="d-none d-sm-block">&nbsp;Feedback</span>
        </>
      );
    return 'Save';
  };

  if (!teammate) return null;

  return (
    <>
      <div className="FeedbackCreationHeader">
        <div className="FeedbackCreationHeader__content">
          <WpButton variant="tertiary" onClick={handleBackClick}>
            <WpIcon name="angle-left" />
            <span className="d-none d-sm-block">Back</span>
          </WpButton>
          <WpButtonGroup>
            {onDeleteDraftClick ? (
              <WpButton
                icon
                qaTest="delete-draft-button"
                variant="tertiary"
                color="danger"
                onClick={setIsDeleteDraftModalOpen}
              >
                <WpIcon name="trash" />
                Delete <span className="d-none d-sm-block">&nbsp;Draft</span>
              </WpButton>
            ) : null}
            {showSaveAsDraftButton && (
              <WpButton
                qaTest="save-as-draft-button"
                loading={submitting.isDraftSaving}
                disabled={saveAsDraftDisabled}
                variant="secondary"
                btnType="submit"
                onClick={handleSaveAsDraftClick}
              >
                Save
                <span className="d-none d-sm-block">&nbsp;as Draft</span>
              </WpButton>
            )}
            {handleNext && (
              <WpButton qaTest="button-feedback-creation-next" onClick={handleNext}>
                Next
              </WpButton>
            )}
            {showSubmit && (
              <WpButton
                qaTest="submit-feedback-button"
                loading={submitting.isSubmitting}
                disabled={submitFeedbackDisabled}
                btnType="submit"
                onClick={handleSubmitFeedbackClick}
              >
                {submitFeedbackButtonLabel()}
              </WpButton>
            )}
          </WpButtonGroup>
        </div>
      </div>
      <ConfirmModal
        isOpen={isDeleteDraftModalOpen}
        onToggle={setIsDeleteDraftModalOpen}
        onConfirmClick={handleDeleteDraftClick}
      >
        <p>Are you sure you want to delete this draft?</p>
        <p className="mb-0">
          <b>Your feedback will be lost.</b>
        </p>
      </ConfirmModal>
    </>
  );
}

export default FeedbackCreationHeader;
